/**
 * Appariement lignes extraites (Claude) ↔ lignes de la last-bill Zoho.
 *
 * POURQUOI : la duplication de structure reprenait le produit et le compte de
 * la dernière facture PAR POSITION (`template[idx]`). Or l'ordre des lignes
 * de Claude suit le PDF courant, tandis que l'ordre du template suit la saisie
 * de l'ancienne facture. Les deux n'ont aucune raison de coïncider → dès que
 * la facture porte DEUX COMPTES DISTINCTS, les montants se retrouvaient sur le
 * mauvais compte (invisible quand toutes les lignes partagent le même compte,
 * d'où un bug longtemps passé inaperçu).
 *
 * On apparie donc par CONTENU :
 *   1. le taux de TVA (discriminant fort et fiable : Zoho le stocke sur la
 *      ligne, Claude le lit sur le PDF) ;
 *   2. la similarité des libellés (les libellés fournisseurs sont très stables
 *      d'un mois sur l'autre — relevés bancaires, abonnements…).
 *
 * En cas d'ÉGALITÉ entre deux lignes template de structures différentes, on
 * renvoie `null` : mieux vaut un compte vide (que l'utilisateur remplit, avec
 * un avertissement à l'écran) qu'un compte faux appliqué silencieusement.
 */

export interface TemplateLineLike {
  item_id: string | null;
  item_name: string | null;
  description: string;
  tax_percentage: number;
  account_name: string | null;
}

export interface ExtractedLineLike {
  description?: string | null;
  vat_rate?: number | null;
}

/** Structure = ce que la duplication reprend réellement (produit + compte). */
function structureKey(t: TemplateLineLike): string {
  return `${t.item_id ?? ''}|${(t.account_name ?? '').trim().toLowerCase()}`;
}

/**
 * Classe de TVA. `null` (non récupérable / non indiquée) et 0 (autoliquidation)
 * sont traités comme équivalents : Zoho renvoie `tax_percentage: 0` pour une
 * ligne sans taxe, alors que Claude renvoie `vat_rate: null`.
 */
function taxClass(rate: number | null | undefined): string {
  return rate == null || rate === 0 ? 'none' : String(rate);
}

function tokenize(s: string | null | undefined): Set<string> {
  if (!s) return new Set();
  const base = s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return new Set(base.split(' ').filter((w) => w.length >= 3));
}

/** Jaccard sur les mots de ≥ 3 lettres. 0 si l'un des deux libellés est vide. */
function similarity(a: string | null | undefined, b: string | null | undefined): number {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const w of ta) if (tb.has(w)) inter++;
  const union = ta.size + tb.size - inter;
  return union === 0 ? 0 : inter / union;
}

/**
 * Score d'appariement d'une ligne extraite avec une ligne template.
 * TVA identique = +2 / TVA différente = −2 (discriminant fort) ;
 * similarité de libellé = 0 à +3.
 */
function score(extracted: ExtractedLineLike, tmpl: TemplateLineLike): number {
  const taxScore = taxClass(extracted.vat_rate) === taxClass(tmpl.tax_percentage) ? 2 : -2;
  const descScore = 3 * similarity(extracted.description, tmpl.description || tmpl.item_name);
  return taxScore + descScore;
}

/**
 * Pour chaque ligne extraite, l'index de la ligne template dont il faut
 * reprendre la structure — ou `null` si l'appariement est ambigu.
 *
 * Quand toutes les lignes du template partagent la même structure, la question
 * ne se pose pas : on renvoie 0 partout (aucun risque d'inversion).
 */
export function matchTemplateLines(
  extractedLines: ExtractedLineLike[],
  template: TemplateLineLike[],
): Array<number | null> {
  if (template.length === 0) return extractedLines.map(() => null);

  const structures = new Set(template.map(structureKey));
  if (structures.size === 1) return extractedLines.map(() => 0);

  return extractedLines.map((line) => {
    const scores = template.map((t) => score(line, t));
    const best = Math.max(...scores);
    const winners = scores
      .map((s, j) => (s === best ? j : -1))
      .filter((j) => j >= 0);

    // Plusieurs templates ex æquo : acceptable seulement s'ils portent la même
    // structure (le choix est alors sans conséquence). Sinon → ambigu.
    const winnerStructures = new Set(winners.map((j) => structureKey(template[j]!)));
    if (winnerStructures.size > 1) return null;
    return winners[0]!;
  });
}
